// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Button, Group, Loader, Pagination, Stack, Table, Text } from '@mantine/core';
import { IconNotes } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ProviderAppointmentDetails } from '../../hooks/useProviderAppointmentDetails';
import classes from '../../pages/schedule/SchedulePage.module.css';
import {
  getAppointmentStatus,
  getAppointmentStatusLabel,
  isCheckInAppointment,
} from '../../utils/appointment-formatting';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';
import { TableAnchor } from '../layout/TableAnchor';

const ITEMS_PER_PAGE = 10;

interface RecentAppointmentsTableProps {
  appointments: ProviderAppointmentDetails[];
  loading: boolean;
  searchQuery: string;
  showProvider?: boolean;
  emptyMessage?: string;
  rangeSummary?: string;
}

function TimeOverdueCell({ details }: { details: ProviderAppointmentDetails }): JSX.Element {
  if (details.noteSigned || details.timeOverdueDays === undefined) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  if (details.timeOverdueDays > 7) {
    return (
      <Badge color="red" variant="light" size="sm">
        {details.timeOverdueDays} days
      </Badge>
    );
  }

  return (
    <Text size="sm">
      {details.timeOverdueDays} day{details.timeOverdueDays !== 1 ? 's' : ''}
    </Text>
  );
}

function statusClass(status: string): string {
  switch (status) {
    case 'fulfilled':
      return classes.statusCompleted;
    case 'rescheduled':
      return classes.statusRescheduled;
    case 'noshow':
      return classes.statusNoShow;
    case 'technical-issues':
      return classes.statusTechnical;
    default:
      return classes.statusCancelled;
  }
}

function getNoteActionLabel(details: ProviderAppointmentDetails): string {
  if (isCheckInAppointment(details.appointment)) {
    return 'Coordinator Note';
  }
  return details.noteSigned ? 'View Note' : 'Complete Note';
}

/**
 * Table displaying recent (last 30 days) fulfilled appointments for the provider.
 * Shows time, patient, type, documents, time overdue for notes, comments, and actions.
 */
export function RecentAppointmentsTable({
  appointments,
  loading,
  searchQuery,
  showProvider = false,
  emptyMessage = 'No recent appointments.',
  rangeSummary,
}: RecentAppointmentsTableProps): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  // Filter appointments by search query
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) {
      return appointments;
    }
    const q = searchQuery.toLowerCase();
    return appointments.filter(
      (d) =>
        d.patientName.toLowerCase().includes(q) ||
        d.providerName.toLowerCase().includes(q) ||
        d.appointmentType.toLowerCase().includes(q) ||
        getAppointmentStatusLabel(d.appointment).toLowerCase().includes(q)
    );
  }, [appointments, searchQuery]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const currentPage = Math.min(page, Math.max(totalPages, 1));
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (loading) {
    return (
      <Stack align="center" py="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  if (appointments.length === 0) {
    return (
      <Text c="dimmed" py="md">
        {emptyMessage}
      </Text>
    );
  }

  return (
    <>
      <Table className={classes.table}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Date &amp; Time</Table.Th>
            <Table.Th>Patient</Table.Th>
            {showProvider && <Table.Th>Provider</Table.Th>}
            <Table.Th>Appointment Type</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Time Overdue</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {paginated.map((details) => (
            <Table.Tr key={details.appointment.id}>
              <Table.Td>
                <AppointmentTimeWithTimezone
                  start={details.appointment.start}
                  end={details.appointment.end}
                  textProps={{ size: 'sm' }}
                  showDate
                />
              </Table.Td>
              <Table.Td>
                <TableAnchor
                  size="sm"
                  onClick={() => {
                    if (details.patientId) {
                      navigate(`/Patient/${details.patientId}`)?.catch(console.error);
                    }
                  }}
                >
                  {details.patientName}
                </TableAnchor>
              </Table.Td>
              {showProvider && <Table.Td>{details.providerName}</Table.Td>}
              <Table.Td>{details.appointmentType}</Table.Td>
              <Table.Td>
                <Badge className={statusClass(getAppointmentStatus(details.appointment))} variant="light" size="sm">
                  {getAppointmentStatusLabel(details.appointment)}
                </Badge>
              </Table.Td>
              <Table.Td>
                <TimeOverdueCell details={details} />
              </Table.Td>
              <Table.Td>
                {getAppointmentStatus(details.appointment) !== 'booked' &&
                details.encounter?.id &&
                details.patientId ? (
                  <Button
                    size="xs"
                    variant="subtle"
                    className={classes.action}
                    leftSection={<IconNotes size={14} />}
                    onClick={() => {
                      navigate(`/Patient/${details.patientId}/Encounter/${details.encounter?.id}`)?.catch(
                        console.error
                      );
                    }}
                  >
                    {getNoteActionLabel(details)}
                  </Button>
                ) : (
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {totalPages > 1 && (
        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">
            {filtered.length} of {appointments.length}
          </Text>
          <Pagination value={currentPage} onChange={setPage} total={totalPages} size="sm" />
        </Group>
      )}
      {rangeSummary && (
        <Text size="sm" c="dimmed" pt="sm">
          {filtered.length} {filtered.length === 1 ? 'appointment' : 'appointments'} · {rangeSummary}
        </Text>
      )}
    </>
  );
}
